import { useId } from 'react';
import { Badge, Form } from 'react-bootstrap';
import {
  FEMA_ENVIRONMENT_LIVE_WARNING,
  FEMA_ENVIRONMENT_LOCKED,
  FEMA_ENVIRONMENT_NOT_AVAILABLE,
  FEMA_ENVIRONMENT_NOT_LOADED,
  FEMA_ENVIRONMENT_SELECT_LABEL,
} from '../../../IpawsAlertFields/config/femaEnvironment.config';
import {
  describeUnavailableFemaEnvironments,
  getDefaultFemaEnvironmentChoice,
  getFemaEnvironmentFinding,
  getFemaEnvironmentOption,
  getFemaEnvironmentOptionStates,
  isFemaEnvironmentChoice,
} from '../../../../utils/femaEnvironment.utils';

/**
 * The FEMA environment select in the IPAWS banner (card FE-01, Mike 2026-09-25; multi-COG
 * contract §2). It shows the three FEMA environment classes.
 * - Multi-COG meta (`environments[]`): a held class that can send is enabled. A held class that
 *   cannot send is disabled, and the help text shows its `reason`. A class that the workplace
 *   does not hold is disabled.
 * - Older meta: only the class that the service serves is enabled.
 * LIVE PRODUCTION carries the danger styling. An Update locks the select to the class of the
 * original message.
 *
 * @param {object} props
 * @param {{ isLoading?: boolean, isError?: boolean, data?: object }} props.femaMetaQuery The
 *   `useGetFemaMetaQuery` result.
 * @param {string|null|undefined} props.value The stored class token (`tdl`, `demo`, `production`).
 * @param {(choice: string) => void} props.onChange Receives the class token that the operator selects.
 * @param {boolean} [props.locked=false] True while an Update is authored: the select is disabled.
 * @param {boolean} [props.showUnavailableNote] Show the "… not available" sentence under the
 *   select. The IPAWS banner turns it off and shows the sentence in its advanced details.
 */
// The label and the select share one line; the select is as wide as its longest option, and a
// finding or the help sentence wraps onto its own line below (w-100). They stay siblings of the
// select so Bootstrap's `.is-invalid ~ .invalid-feedback` rule still shows the finding.
const GROUP_CLASS = 'mt-2 d-flex flex-wrap align-items-center column-gap-2 row-gap-1';
const LABEL_CLASS = 'small fw-semibold mb-0';
const SELECT_CLASS = 'w-auto mw-100';
/** The LIVE PRODUCTION styling of the select when production is the selected class. */
const LIVE_PRODUCTION_CLASS = 'border-danger text-danger fw-bold';

const FemaEnvironmentSelect = ({
  femaMetaQuery,
  value,
  onChange,
  locked = false,
  showUnavailableNote = true,
}) => {
  const selectId = useId();
  const meta = femaMetaQuery?.data;
  const isLoaded = Boolean(meta) && !femaMetaQuery?.isLoading && !femaMetaQuery?.isError;

  if (!isLoaded) {
    return (
      <Form.Group className={GROUP_CLASS} controlId={selectId}>
        <Form.Label className={LABEL_CLASS}>{FEMA_ENVIRONMENT_SELECT_LABEL}</Form.Label>
        <Form.Select size="sm" className={SELECT_CLASS} disabled value="" onChange={() => {}}>
          <option value="">{FEMA_ENVIRONMENT_NOT_LOADED}</option>
        </Form.Select>
      </Form.Group>
    );
  }

  const hasStoredValue = value !== null && value !== undefined && value !== '';
  const selected = hasStoredValue ? value : (getDefaultFemaEnvironmentChoice(meta) ?? '');
  const isInvalid = hasStoredValue && getFemaEnvironmentFinding(value, meta) !== null;
  const optionStates = getFemaEnvironmentOptionStates(meta);
  const unavailableSentence = showUnavailableNote ? describeUnavailableFemaEnvironments(meta) : '';
  const isLiveProduction = Boolean(getFemaEnvironmentOption(selected)?.isProduction);
  const helpText = locked ? FEMA_ENVIRONMENT_LOCKED : unavailableSentence;
  const helpId = `${selectId}-help`;
  const findingId = `${selectId}-finding`;

  const handleChange = ({ target }) => {
    if (!locked && isFemaEnvironmentChoice(target.value)) onChange(target.value);
  };

  return (
    <Form.Group className={GROUP_CLASS} controlId={selectId}>
      <Form.Label className={LABEL_CLASS}>{FEMA_ENVIRONMENT_SELECT_LABEL}</Form.Label>
      <Form.Select
        size="sm"
        className={isLiveProduction ? `${SELECT_CLASS} ${LIVE_PRODUCTION_CLASS}` : SELECT_CLASS}
        value={selected}
        onChange={handleChange}
        disabled={locked}
        isInvalid={isInvalid}
        aria-describedby={
          [isInvalid ? findingId : null, helpText ? helpId : null].filter(Boolean).join(' ') ||
          undefined
        }
      >
        {isFemaEnvironmentChoice(selected) ? null : <option value={selected}>{selected}</option>}
        {optionStates.map(({ option, enabled, text }) => (
          <option
            key={option.value}
            value={option.value}
            disabled={!enabled}
            className={option.isProduction ? 'text-danger fw-bold' : undefined}
          >
            {text}
          </option>
        ))}
      </Form.Select>
      {isInvalid ? (
        <Form.Control.Feedback type="invalid" id={findingId} className="w-100">
          {FEMA_ENVIRONMENT_NOT_AVAILABLE}
        </Form.Control.Feedback>
      ) : null}
      {isLiveProduction ? (
        <div
          className="w-100 mt-1 d-flex align-items-center gap-2 text-danger fw-semibold"
          role="note"
        >
          <Badge bg="danger">LIVE PRODUCTION</Badge>
          <span>{FEMA_ENVIRONMENT_LIVE_WARNING}</span>
        </div>
      ) : null}
      {helpText ? (
        <Form.Text id={helpId} className="d-block w-100">
          {helpText}
        </Form.Text>
      ) : null}
    </Form.Group>
  );
};

export default FemaEnvironmentSelect;
